/**
 * Navigate's Audio section (2026-10-13, the audio library; operator: "an audio files section in the navigate menu
 * that the user can import, manage, edit, delete etc audio files ... sectioned, one shots, loops, and songs, user can
 * drag an audio file into the section and a process module pops up, if there a multiple, it'd be a modal queue").
 *
 * One list of every audio file OPBridge keeps for this account, in three parts: One-shots, Loops, Songs (the
 * operator's type, else a guess from the analysis: `timeline/audioKind.ts`). Drop files on a part to import them as
 * that type (the processing window, `SongImport.tsx`, shows the queue); drag a row onto another part to change its
 * type. Per file: listen, mark sections (songs), rename, change type, replace (same file everywhere it is used),
 * delete (warns, naming every timeline that uses it). Built only from THE NAVIGATE KIT (DEC-255); `nv-drop` and
 * `nv-wave` were added to the kit for it. Type and name changes and replace need an OPBridge that keeps the audio
 * library (`audio_library`, asked 2026-10-13); until then the section says so instead of pretending.
 */
import { createEffect, createSignal, For, onCleanup, onMount, Show, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import type { AudioKind, TestTrack } from '../opbridge/httpClient.ts';
import {
  audioLibraryReady, beatmaps, deleteAudio, enqueueAudio, importModalOpen, loadTrack, patchAudio, peaksById, play, refreshTracks,
  status, stop, tracks, unavailableReason,
} from '../timeline/testTrackStore.ts';
import { KIND_LABEL, KIND_PLURAL, KINDS, kindOf } from '../timeline/audioKind.ts';
import { gridBpm, waveformPath } from '../timeline/testTrack.ts';
import { timelines } from '../timeline/store.ts';
import { primaryBridge } from '../opbridge/store.ts';
import { pushToast } from '../ui/toast.ts';
import { SongImportModal } from './SongImport.tsx';
import { SongMarkRow } from './SongMarker.tsx';

/** A row being dragged to another part (its id). */
const DRAG_TYPE = 'application/x-opbuttons-audio';
const HINT: Record<AudioKind, string> = {
  oneshot: 'Short sounds that play once.',
  loop: 'Repeat in time with the clock.',
  song: 'Whole tracks, with sections to mark.',
};
const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const fmtBytes = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

let pickerEl: HTMLInputElement | undefined;
/** Navigate's "Import audio..." button (PANE_DEFS) opens the file picker. */
export function openAudioPicker(): void { pickerEl?.click(); }

export function AudioPane(): JSX.Element {
  onMount(() => { void refreshTracks(); });
  // Beat maps and waveforms for every analysed file: the type guess, the tempo and the small waveform need them.
  createEffect(() => { for (const t of tracks()) if (t.analysed && !(t.id in beatmaps())) void loadTrack(t.id); });
  const kind = (t: TestTrack) => kindOf(t, beatmaps()[t.id]);
  const usedBy = (id: string) => timelines().filter((tl) => tl.testTrack?.id === id);
  const [over, setOver] = createSignal<AudioKind | null>(null);
  const [tools, setTools] = createSignal<string | null>(null);
  const [renaming, setRenaming] = createSignal<string | null>(null);
  const [marking, setMarking] = createSignal<string | null>(null);
  const [deleting, setDeleting] = createSignal<string | null>(null);
  const [replacing, setReplacing] = createSignal<{ id: string; file: File } | null>(null);
  let replaceEl: HTMLInputElement | undefined;
  let replaceFor: string | null = null;
  let markBody: HTMLDivElement | undefined;
  const [markW, setMarkW] = createSignal(700);
  const playingId = () => { const s = status(); return s?.playing ? s.id : null; };
  const total = () => tracks().reduce((n, t) => n + (t.size ?? 0), 0);
  const nameOf = (id: string | null) => tracks().find((t) => t.id === id)?.name ?? '';

  // Esc closes the delete or replace sheet on top (the marking view and the processing window close themselves). The
  // sheets mount in Navigate's own layer (shellPortal), so Navigate's Esc waits instead of closing everything.
  {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || (!deleting() && !replacing())) return;
      e.preventDefault(); e.stopPropagation();
      setDeleting(null); setReplacing(null);
    };
    // On mount, AFTER Navigate's own listener (it mounts around this section): on one target, listeners run in the
    // order they were added, and stopPropagation does not stop the others. Added while rendering, this ran first,
    // removed the sheet, and Navigate's Esc then saw no window over it and closed everything (found 2026-10-13).
    onMount(() => window.addEventListener('keydown', onKey, { capture: true }));
    onCleanup(() => window.removeEventListener('keydown', onKey, { capture: true }));
  }
  const needsUpdate = () => pushToast('warn', 'This OPBridge cannot keep a type, a name or a replaced file yet. It will once it is updated.');
  function moveTo(id: string, k: AudioKind) {
    const t = tracks().find((x) => x.id === id);
    if (!t || kind(t) === k) return;
    if (!audioLibraryReady()) { needsUpdate(); return; }
    void patchAudio(id, { kind: k });
  }
  function onDrop(e: DragEvent, k: AudioKind) {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer?.getData(DRAG_TYPE);
    if (id) { moveTo(id, k); return; }
    const files = [...(e.dataTransfer?.files ?? [])];
    if (files.length) void enqueueAudio(files, { kind: k });
  }
  function listen(t: TestTrack) {
    if (playingId() === t.id) { void stop(); return; }
    const pc = primaryBridge()?.nodeId;
    if (!pc) { pushToast('warn', 'No OPBridge is connected to play it on.'); return; }
    void play({ id: t.id, pc, output: '', fromBar: 1 });
  }
  function openMark(id: string) {
    setMarking(id);
    queueMicrotask(() => { if (markBody) setMarkW(Math.max(320, markBody.clientWidth - 300)); });
  }

  const detail = (t: TestTrack): string => {
    if (t.arriving) return `arriving from another computer, ${Math.round(t.arriving.pct)}%`;
    if (t.analysisError) return `could not be analysed: ${t.analysisError}`;
    if (t.queued) return 'waiting to be analysed';
    if (t.analysing || !t.analysed) return 'analysing...';
    const m = beatmaps()[t.id];
    const used = usedBy(t.id).length;
    return [
      fmtTime(t.durationSec),
      m ? `${gridBpm(m).toFixed(1)} BPM` : null,
      t.size ? fmtBytes(t.size) : null,
      used ? `used by ${used} timeline${used === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' · ');
  };

  return (
    <div class="nav-pane-body nav-audio" role="region" aria-label="Audio" data-testid="nav-audio">
      <input ref={(el) => { pickerEl = el; }} type="file" multiple accept="audio/*,.mp3,.wav,.flac,.aif,.aiff,.ogg,.m4a" style={{ display: 'none' }}
        onChange={(e) => { const fs = [...(e.currentTarget.files ?? [])]; e.currentTarget.value = ''; if (fs.length) void enqueueAudio(fs); }} />
      <input ref={replaceEl} type="file" accept="audio/*,.mp3,.wav,.flac,.aif,.aiff,.ogg,.m4a" style={{ display: 'none' }}
        onChange={(e) => { const f = e.currentTarget.files?.[0]; e.currentTarget.value = ''; if (f && replaceFor) setReplacing({ id: replaceFor, file: f }); }} />
      <Show when={unavailableReason()}>
        <p class="nv-note" data-tone="bad">{unavailableReason()}</p>
      </Show>
      <Show when={!unavailableReason() && !audioLibraryReady()}>
        <p class="nv-note">Importing, listening, marking and deleting work now. Changing a file's type or name, and replacing it, wait for an OPBridge update.</p>
      </Show>
      <For each={KINDS}>
        {(k) => {
          const rows = () => tracks().filter((t) => kind(t) === k);
          return (
            <section class="nv-ss" data-kind={k} data-testid={`aud-${k}`}>
              <div class="nv-ss-h"><h2>{KIND_PLURAL[k]}</h2><p>{HINT[k]}</p><span class="nv-sp" /><span class="nv-ct">{rows().length}</span></div>
              <div
                class="nav-well nv-drop"
                data-over={over() === k ? 'true' : undefined}
                onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = e.dataTransfer.types.includes(DRAG_TYPE) ? 'move' : 'copy'; setOver(k); }}
                onDragLeave={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null); }}
                onDrop={(e) => onDrop(e, k)}
              >
                <Show when={rows().length} fallback={<p class="nv-drop-hint">Drop audio files here to add them as {KIND_PLURAL[k].toLowerCase()}.</p>}>
                  <For each={rows()}>
                    {(t) => (
                      <div class="aud-item" data-testid="aud-row" data-id={t.id} data-kind={k}>
                        <div class="nav-lr" draggable={t.analysed} onDragStart={(e) => { e.dataTransfer?.setData(DRAG_TYPE, t.id); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; }}>
                          <span class="nav-lr-main">
                            <Show when={renaming() === t.id} fallback={<b>{t.name}</b>}>
                              <span class="nv-fld" data-nav-layer><input
                                aria-label="Name"
                                value={t.name}
                                ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') { setRenaming(null); return; }
                                  if (e.key !== 'Enter') return;
                                  const v = e.currentTarget.value.trim();
                                  setRenaming(null);
                                  if (v && v !== t.name) void patchAudio(t.id, { name: v });
                                }}
                                onBlur={() => setRenaming(null)}
                              /></span>
                            </Show>
                            <small>{detail(t)}</small>
                          </span>
                          <Show when={peaksById()[t.id]}>
                            {(pk) => <svg class="nv-wave" viewBox="0 0 120 24" preserveAspectRatio="none" aria-hidden="true"><path d={waveformPath(pk(), 0, t.durationSec, 120, 24)} /></svg>}
                          </Show>
                          <span class="nav-lr-trail">
                            <button type="button" class="nv-b nv-b-sm" disabled={!t.analysed} data-testid="aud-play" onClick={() => listen(t)}>{playingId() === t.id ? 'Stop' : 'Listen'}</button>
                            <Show when={k === 'song'}>
                              <button type="button" class="nv-b nv-b-sm" disabled={!t.analysed} data-testid="aud-mark" onClick={() => openMark(t.id)}>Mark sections</button>
                            </Show>
                            <button type="button" class="nv-b nv-b-sm nv-b-q" aria-expanded={tools() === t.id} data-testid="aud-more" onClick={() => setTools(tools() === t.id ? null : t.id)}>More</button>
                          </span>
                        </div>
                        <Show when={tools() === t.id}>
                          <div class="nv-row aud-tools" data-testid="aud-tools">
                            <span class="nv-seg" role="radiogroup" aria-label="Type">
                              <For each={KINDS}>{(kk) => <button type="button" role="radio" aria-pressed={kind(t) === kk} onClick={() => moveTo(t.id, kk)}>{KIND_LABEL[kk]}</button>}</For>
                            </span>
                            <button type="button" class="nv-b nv-b-sm" onClick={() => { if (!audioLibraryReady()) { needsUpdate(); return; } setRenaming(t.id); }}>Rename</button>
                            <button type="button" class="nv-b nv-b-sm" onClick={() => { if (!audioLibraryReady()) { needsUpdate(); return; } replaceFor = t.id; replaceEl?.click(); }}>Replace...</button>
                            <button type="button" class="nv-b nv-b-sm" data-testid="aud-delete" onClick={() => setDeleting(t.id)}>Delete</button>
                          </div>
                        </Show>
                      </div>
                    )}
                  </For>
                </Show>
              </div>
            </section>
          );
        }}
      </For>
      <Show when={total() > 0}>
        <p class="nv-note">{tracks().length} files, {fmtBytes(total())}. Every file is on every computer in the rig, so smaller files (MP3, FLAC) copy faster than WAV.</p>
      </Show>

      {/* the processing window (the queue), over this section */}
      <Show when={importModalOpen()}>
        <Portal mount={shellPortal() ?? document.body}>
          <div class="tl2 aud-modal" style={{ display: 'contents' }}>
            <SongImportModal onMark={(id) => openMark(id)} />
          </div>
        </Portal>
      </Show>

      <Show when={deleting()}>
        {(id) => (<Portal mount={shellPortal() ?? document.body}>
          <div class="nv-sheet-scrim" role="dialog" aria-modal="true" aria-label="Delete audio" data-testid="aud-delete-sheet">
            <div class="nv-sheet">
              <div class="nv-sheet-h"><h2>Delete {nameOf(id())}?</h2><p>It goes from every computer in the rig. This cannot be undone.</p></div>
              <div class="nv-sheet-b">
                <Show when={usedBy(id()).length} fallback={<p class="nv-note">Nothing uses it.</p>}>
                  <p class="nv-note" data-tone="bad" data-testid="aud-in-use">In use by {usedBy(id()).map((tl) => `"${tl.name}"`).join(', ')}. {usedBy(id()).length === 1 ? 'It' : 'They'} will have no song until you choose another.</p>
                </Show>
              </div>
              <div class="nv-sheet-f"><div class="nv-row">
                <span class="nv-sp" />
                <button type="button" class="nv-b" onClick={() => setDeleting(null)}>Cancel</button>
                <button type="button" class="nv-b nv-b-pri" data-testid="aud-delete-yes" onClick={() => { const x = id(); setDeleting(null); setTools(null); void deleteAudio(x); }}>Delete</button>
              </div></div>
            </div>
          </div>
        </Portal>)}
      </Show>

      <Show when={replacing()}>
        {(r) => (<Portal mount={shellPortal() ?? document.body}>
          <div class="nv-sheet-scrim" role="dialog" aria-modal="true" aria-label="Replace audio">
            <div class="nv-sheet">
              <div class="nv-sheet-h"><h2>Replace {nameOf(r().id)} with {r().file.name}?</h2><p>Everything that uses it keeps using it, with the new audio. It is analysed again.</p></div>
              <div class="nv-sheet-b"><p class="nv-note">Keep its marked sections if the new file is the same song (a better copy, a new mix); clear them if it is different.</p></div>
              <div class="nv-sheet-f"><div class="nv-row">
                <span class="nv-sp" />
                <button type="button" class="nv-b" onClick={() => setReplacing(null)}>Cancel</button>
                <button type="button" class="nv-b" onClick={() => { const x = r(); setReplacing(null); void enqueueAudio([x.file], { replaceOf: x.id, keep: false }); }}>Replace, clear sections</button>
                <button type="button" class="nv-b nv-b-pri" onClick={() => { const x = r(); setReplacing(null); void enqueueAudio([x.file], { replaceOf: x.id, keep: true }); }}>Replace, keep sections</button>
              </div></div>
            </div>
          </div>
        </Portal>)}
      </Show>

      <Show when={marking()}>
        {(id) => (<Portal mount={shellPortal() ?? document.body}>
          <div class="nv-sheet-scrim" role="dialog" aria-modal="true" aria-label="Mark sections" data-testid="aud-mark-sheet">
            <div class="nv-sheet aud-mark-sheet">
              <div class="nv-sheet-h"><h2>The parts of {nameOf(id())}</h2><p>Drag across the song, then say what that part is. Drag a part to move it; click to listen from there. Every timeline using this song follows.</p></div>
              <div class="nv-sheet-b" ref={markBody}>
                <div class="tl2 aud-marker" style={{ display: 'contents' }}>
                  <SongMarkRow trackId={id()} width={markW()} onDone={() => setMarking(null)} />
                </div>
              </div>
            </div>
          </div>
        </Portal>)}
      </Show>
    </div>
  );
}
